/**
 * Comment-preserving editor for skaile.yaml files.
 *
 * Uses the `yaml` library's Document API to parse into an AST that retains
 * comments, blank lines, and formatting. Mutations are applied to the AST
 * and serialized back — untouched sections remain byte-identical.
 *
 * Usage:
 *
 *   import { WorkspaceYamlEditor } from '@skaile/workspaces/core'
 *
 *   const editor = WorkspaceYamlEditor.load('/project/skaile.yaml')
 *   editor.setAgentConfig('default', { driver: 'claude-sdk', model: 'claude-sonnet-4-6' })
 *   editor.setSource({ url: 'https://github.com/skaile-ai/ai-assets' })
 *   editor.save()
 */

import { existsSync, readFileSync, writeFileSync } from "node:fs";
import { Document, isMap, isSeq, parseDocument, type YAMLMap } from "yaml";
import type {
  AgentConfigProfile,
  ConnectorDeclaration,
  McpServerDeclaration,
  OverrideEntry,
  SourceEntry,
  StoreEntry,
} from "./workspace-config.js";

/**
 * Comment-preserving editor for `skaile.yaml` files.
 * Uses the `yaml` library's Document AST so untouched sections remain byte-identical.
 * Create with `WorkspaceYamlEditor.load(filePath)` or `WorkspaceYamlEditor.create(filePath)`.
 * @docLink packages/core/workspace-config#workspace-yaml-editor
 */
export class WorkspaceYamlEditor {
  readonly path: string;
  private doc: Document;

  private constructor(path: string, doc: Document) {
    this.path = path;
    this.doc = doc;
  }

  /** Load an existing skaile.yaml file. */
  static load(filePath: string): WorkspaceYamlEditor {
    if (!existsSync(filePath)) {
      throw new Error(`File not found: ${filePath}`);
    }
    const raw = readFileSync(filePath, "utf-8");
    const doc = parseDocument(raw);
    return new WorkspaceYamlEditor(filePath, doc);
  }

  /** Create a new empty document (for use when no file exists yet). */
  static create(filePath: string): WorkspaceYamlEditor {
    const doc = new Document({});
    return new WorkspaceYamlEditor(filePath, doc);
  }

  /** Write the document back to disk, preserving comments and formatting. */
  save(): void {
    writeFileSync(this.path, this.doc.toString());
  }

  /** Return the serialized YAML string without writing to disk. */
  toString(): string {
    return this.doc.toString();
  }

  // ── Scalar fields ──────────────────────────────────────────────────────────

  /** Get a top-level scalar field (name, description). */
  get(key: "name" | "description"): string | undefined {
    const val = this.doc.get(key);
    return val != null ? String(val) : undefined;
  }

  /** Set a top-level scalar field. */
  set(key: "name" | "description", value: string): void {
    this.doc.set(key, value);
  }

  // ── Agent config ──────────────────────────────────────────────────────────

  /**
   * Get an agent config profile by name (usually "default").
   * Reads from `agent-config` (canonical YAML key).
   */
  getAgentConfig(profile = "default"): AgentConfigProfile | undefined {
    const section = this._getAgentConfigSection();
    if (!section) return undefined;
    const val = section.get(profile);
    return val && typeof val === "object" ? (val as unknown as AgentConfigProfile) : undefined;
  }

  /**
   * Set fields on an agent config profile. Merges with existing values —
   * only the provided fields are overwritten.
   */
  setAgentConfig(profile: string, values: Partial<AgentConfigProfile>): void {
    let section = this._getAgentConfigSection();
    if (!section) {
      // Create the section. Use 'agent-config' as the canonical YAML key.
      this.doc.set("agent-config", { [profile]: {} });
      section = this._getAgentConfigSection()!;
    }

    const existing = section.get(profile, true);
    if (!existing || !isMap(existing)) {
      section.set(profile, { ...values });
      return;
    }

    for (const [k, v] of Object.entries(values)) {
      if (v === undefined) continue;
      existing.set(k, v);
    }
  }

  /**
   * Delete a field from an agent config profile.
   */
  deleteAgentConfigField(profile: string, field: string): void {
    const section = this._getAgentConfigSection();
    if (!section) return;
    const profileNode = section.get(profile, true);
    if (isMap(profileNode)) {
      profileNode.delete(field);
    }
  }

  private _getAgentConfigSection(): YAMLMap | undefined {
    // Try canonical 'agent-config' first, then 'agent_config'
    const node = this.doc.get("agent-config", true) ?? this.doc.get("agent_config", true);
    return isMap(node) ? (node as YAMLMap) : undefined;
  }

  // ── Connectors ─────────────────────────────────────────────────────────────

  /** Get all connectors. */
  getConnectors(): ConnectorDeclaration[] {
    const node = this.doc.get("connectors", true) ?? this.doc.get("data_resources", true);
    if (!isSeq(node)) return [];
    return node.toJSON() as ConnectorDeclaration[];
  }

  /** Add or update a connector by id. */
  setConnector(connector: ConnectorDeclaration): void {
    const key = "connectors";
    const node = this.doc.get(key, true);

    if (!node || !isSeq(node)) {
      this.doc.set(key, this.doc.createNode([connector]));
      return;
    }

    let found = false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("id") === connector.id) {
        node.set(i, this.doc.createNode(connector));
        found = true;
        break;
      }
    }

    if (!found) {
      node.add(this.doc.createNode(connector));
    }
  }

  /** Remove a connector by id. */
  removeConnector(id: string): boolean {
    const key = this.doc.has("connectors") ? "connectors" : "data_resources";
    const node = this.doc.get(key, true);
    if (!node || !isSeq(node)) return false;

    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("id") === id) {
        node.delete(i);
        return true;
      }
    }
    return false;
  }

  // ── MCP servers ────────────────────────────────────────────────────────────

  /**
   * Add or replace an mcp server entry, matched by `id`.
   * Mirrors {@link setConnector}: an existing entry with the same `id` is
   * replaced in place; otherwise the declaration is appended.
   */
  setMcpServer(mcp: McpServerDeclaration): void {
    const key = "mcp_servers";
    const node = this.doc.get(key, true);

    if (!node || !isSeq(node)) {
      this.doc.set(key, this.doc.createNode([mcp]));
      return;
    }

    let found = false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("id") === mcp.id) {
        node.set(i, this.doc.createNode(mcp));
        found = true;
        break;
      }
    }

    if (!found) {
      node.add(this.doc.createNode(mcp));
    }
  }

  // ── Dependencies ───────────────────────────────────────────────────────────

  /**
   * Append a dependency ref to `dependencies:`, deduped by exact ref string.
   *
   * @param ref - Canonical dependency ref (e.g. `skill:@skaile/foo#1.0.0`).
   * @returns `true` when the ref was newly added, `false` when it was already present.
   */
  addDependency(ref: string): boolean {
    const node = this.doc.get("dependencies", true);
    if (!node || !isSeq(node)) {
      this.doc.set("dependencies", this.doc.createNode([ref]));
      return true;
    }
    for (const item of node.items) {
      const cur = item != null && typeof item === "object" && "value" in item ? item.value : item;
      if (cur === ref) return false;
    }
    node.add(this.doc.createNode(ref));
    return true;
  }

  // ── Plugins ────────────────────────────────────────────────────────────────

  /** Get all `plugins:` specifiers. */
  getPlugins(): string[] {
    const node = this.doc.get("plugins", true);
    if (!isSeq(node)) return [];
    const out: string[] = [];
    for (const item of node.items) {
      const cur = item != null && typeof item === "object" && "value" in item ? item.value : item;
      if (typeof cur === "string") out.push(cur);
    }
    return out;
  }

  /**
   * Add a plugin specifier to `plugins:`, deduped by package name.
   * If an entry with the same package name exists it is replaced (so a version
   * bump re-pins in place); otherwise the spec is appended.
   *
   * @param spec - npm specifier, e.g. `"@skaile/provider-fly@^0.1.0"`.
   * @param nameOf - Extracts the package name from a spec (caller passes `parseSpec`-derived names).
   * @returns `true` when an existing entry was replaced, `false` when appended.
   */
  addPlugin(spec: string, nameOf: (s: string) => string): boolean {
    const node = this.doc.get("plugins", true);
    if (!node || !isSeq(node)) {
      this.doc.set("plugins", this.doc.createNode([spec]));
      return false;
    }

    const wantName = nameOf(spec);
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      const cur = item != null && typeof item === "object" && "value" in item ? item.value : item;
      if (typeof cur === "string" && nameOf(cur) === wantName) {
        node.set(i, this.doc.createNode(spec));
        return true;
      }
    }
    node.add(this.doc.createNode(spec));
    return false;
  }

  /**
   * Remove the `plugins:` entry whose package name matches `name`.
   *
   * @param name - Package name to remove (e.g. `"@skaile/provider-fly"`).
   * @param nameOf - Extracts the package name from a spec.
   * @returns `true` when an entry was removed.
   */
  removePlugin(name: string, nameOf: (s: string) => string): boolean {
    const node = this.doc.get("plugins", true);
    if (!node || !isSeq(node)) return false;

    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      const cur = item != null && typeof item === "object" && "value" in item ? item.value : item;
      if (typeof cur === "string" && nameOf(cur) === name) {
        node.delete(i);
        return true;
      }
    }
    return false;
  }

  // ── Sources ────────────────────────────────────────────────────────────────

  /** Get all source entries (`{url, pin?}`). */
  getSources(): SourceEntry[] {
    const node = this.doc.get("sources", true);
    if (!isSeq(node)) return [];
    return node.toJSON() as SourceEntry[];
  }

  /**
   * Add or update a source entry, matched by `url`.
   * If an entry with the same url exists, it is replaced.
   */
  setSource(entry: SourceEntry): void {
    const node = this.doc.get("sources", true);
    if (!node || !isSeq(node)) {
      this.doc.set("sources", this.doc.createNode([entry]));
      return;
    }
    let found = false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("url") === entry.url) {
        node.set(i, this.doc.createNode(entry));
        found = true;
        break;
      }
    }
    if (!found) node.add(this.doc.createNode(entry));
  }

  /** Remove a source entry by url. Returns true when an entry was removed. */
  removeSource(url: string): boolean {
    const node = this.doc.get("sources", true);
    if (!node || !isSeq(node)) return false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("url") === url) {
        node.delete(i);
        return true;
      }
    }
    return false;
  }

  // ── Stores ─────────────────────────────────────────────────────────────────

  /** Get all store entries (`{url}`). */
  getStores(): StoreEntry[] {
    const node = this.doc.get("stores", true);
    if (!isSeq(node)) return [];
    return node.toJSON() as StoreEntry[];
  }

  /** Add or update a store entry, matched by `url`. */
  setStore(entry: StoreEntry): void {
    const node = this.doc.get("stores", true);
    if (!node || !isSeq(node)) {
      this.doc.set("stores", this.doc.createNode([entry]));
      return;
    }
    let found = false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("url") === entry.url) {
        node.set(i, this.doc.createNode(entry));
        found = true;
        break;
      }
    }
    if (!found) node.add(this.doc.createNode(entry));
  }

  /** Remove a store entry by url. Returns true when an entry was removed. */
  removeStore(url: string): boolean {
    const node = this.doc.get("stores", true);
    if (!node || !isSeq(node)) return false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("url") === url) {
        node.delete(i);
        return true;
      }
    }
    return false;
  }

  // ── Overrides ────────────────────────────────────────────────────────────────

  /** Get all override entries (`{ref, source, reason}`). */
  getOverrides(): OverrideEntry[] {
    const node = this.doc.get("overrides", true);
    if (!isSeq(node)) return [];
    return node.toJSON() as OverrideEntry[];
  }

  /** Add or update an override entry, matched by `ref`. */
  setOverride(entry: OverrideEntry): void {
    const node = this.doc.get("overrides", true);
    if (!node || !isSeq(node)) {
      this.doc.set("overrides", this.doc.createNode([entry]));
      return;
    }
    let found = false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("ref") === entry.ref) {
        node.set(i, this.doc.createNode(entry));
        found = true;
        break;
      }
    }
    if (!found) node.add(this.doc.createNode(entry));
  }

  /** Remove an override entry by ref. Returns true when an entry was removed. */
  removeOverride(ref: string): boolean {
    const node = this.doc.get("overrides", true);
    if (!node || !isSeq(node)) return false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (isMap(item) && (item as YAMLMap).get("ref") === ref) {
        node.delete(i);
        return true;
      }
    }
    return false;
  }

  // ── Raw access (escape hatch) ─────────────────────────────────────────────

  /** Get the underlying yaml Document for advanced manipulation. */
  get document(): Document {
    return this.doc;
  }
}
